import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ClipboardList, Building2, MessageSquare, BarChart3, GitPullRequest, Users } from "lucide-react";
import { API, ROLE_LABELS, formatError } from "@/components/admin/constants";
import { LeadsView } from "@/components/admin/LeadsView";
import { PropertiesView } from "@/components/admin/PropertiesView";
import { ChatView } from "@/components/admin/ChatView";
import { PerformanceView } from "@/components/admin/PerformanceView";
import { RequestsView } from "@/components/admin/RequestsView";
import { TeamSettings } from "@/components/admin/TeamSettings";

const TABS = [
  { key: "leads", label: "Leads", icon: ClipboardList, roles: ["cofounder", "manager", "staff"] },
  { key: "properties", label: "Properties", icon: Building2, roles: ["cofounder", "manager", "staff"] },
  { key: "chat", label: "Chat", icon: MessageSquare, roles: ["cofounder", "manager", "staff"] },
  { key: "performance", label: "Performance", icon: BarChart3, roles: ["cofounder", "manager"] },
  { key: "requests", label: "Requests", icon: GitPullRequest, roles: ["cofounder"] },
  { key: "team", label: "Team", icon: Users, roles: ["cofounder"] },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [view, setView] = useState("leads");
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user && user.role === "cofounder") {
      const fetchCount = () => {
        axios
          .get(`${API}/change-requests`, { withCredentials: true })
          .then((res) => setPendingCount(res.data.filter((r) => r.status === "pending").length))
          .catch(() => {});
      };
      fetchCount();
      const t = setInterval(fetchCount, 15000);
      return () => clearInterval(t);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const poll = () => {
      axios
        .get(`${API}/chat/unread`, { withCredentials: true })
        .then((res) => {
          if (view === "chat") {
            setUnreadChat(0);
            return;
          }
          setUnreadChat(res.data.count || 0);
        })
        .catch(() => {});
    };
    if (view === "chat") {
      setUnreadChat(0);
    }
    poll();
    const t = setInterval(poll, 12000);
    return () => clearInterval(t);
  }, [user, view]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setView("leads");
    setUnreadChat(0);
    setPendingCount(0);
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500" data-testid="admin-loading">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-6">
        <div className="w-full max-w-md rounded-xl border border-border bg-white p-10 shadow-soft">
          <div className="flex items-center gap-3">
            <img src="/assets/veera-tripod.png" alt="Veera Group" className="h-9 w-auto" />
            <span className="font-display text-lg font-extrabold tracking-tight text-navy">URBAN VEERA</span>
          </div>
          <h1 className="mt-8 font-display text-2xl font-extrabold tracking-tight text-navy" data-testid="admin-login-heading">
            Team Login
          </h1>
          <form onSubmit={login} className="mt-8 space-y-7" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Email</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="field-input mt-2"
                placeholder="you@veeragroup.in"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">Password</label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="field-input mt-2"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p className="text-sm font-semibold text-red-600" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              className="w-full rounded-md bg-amber px-8 py-3.5 text-sm font-bold text-navy transition-colors duration-300 hover:bg-amberdark hover:text-white"
            >
              Sign In
            </button>
          </form>
          <Link to="/" className="mt-7 block text-xs font-bold uppercase tracking-[0.18em] text-gray-500 transition-colors duration-300 hover:text-amberdark" data-testid="admin-back-link">
            ← Back to site
          </Link>
        </div>
      </div>
    );
  }

  const visibleTabs = TABS.filter((t) => t.roles.includes(user.role));
  const activeTab = visibleTabs.find((t) => t.key === view) || visibleTabs[0];

  return (
    <div className="min-h-screen bg-cream px-6 py-10 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1500px]">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-white p-6 shadow-soft">
          <div className="flex items-center gap-4">
            <img src="/assets/veera-tripod.png" alt="Veera Group" className="h-10 w-auto" />
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amberdark">Urban Veera — Internal</p>
              <h1 className="font-display text-2xl font-extrabold tracking-tight text-navy" data-testid="admin-heading">
                {activeTab.label}
              </h1>
              <p className="mt-0.5 text-xs font-semibold text-gray-500" data-testid="logged-in-user">
                {user.name} · {ROLE_LABELS[user.role] || "Staff"}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="rounded-md border-2 border-border px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-gray-500 transition-colors duration-300 hover:border-navy hover:text-navy"
          >
            Logout
          </button>
        </div>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="view-tabs">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setView(t.key)}
              data-testid={`view-tab-${t.key}`}
              className={`flex items-center gap-2 rounded-md border px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-200 ${
                activeTab.key === t.key ? "border-navy bg-navy text-white" : "border-border bg-white text-gray-600 hover:border-navy"
              }`}
            >
              <t.icon size={14} />
              {t.label}
              {t.key === "requests" && pendingCount > 0 && (
                <span className="rounded-full bg-amber px-2 py-0.5 text-[10px] font-bold text-navy" data-testid="requests-badge">
                  {pendingCount}
                </span>
              )}
              {t.key === "chat" && unreadChat > 0 && (
                <span className="rounded-full bg-amber px-2 py-0.5 text-[10px] font-bold text-navy" data-testid="chat-badge">
                  {unreadChat}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab.key === "leads" && <LeadsView user={user} />}
        {activeTab.key === "properties" && <PropertiesView user={user} />}
        {activeTab.key === "chat" && <ChatView user={user} />}
        {activeTab.key === "performance" && <PerformanceView />}
        {activeTab.key === "requests" && <RequestsView user={user} />}
        {activeTab.key === "team" && <TeamSettings user={user} />}
      </div>
    </div>
  );
}
